import type {
  PolicyholderVO,
  BeneficiaryVO,
  OrderFinancialVO,
  OrderProductPlanVO,
  RiderVO,
  OrderSignInfo,
  InsuredDTO,
  BasicInfo,
  PersonalInfo,
  WorkInfo,
  TaxInfo,
  Employer,
  CurrentAssets,
  MonthlyExpense,
  MonthlyIncome,
  Liabilities,
  NonCurrentAssets,
  CurrentProtection,
} from "./orders";

// Factory functions to create independent object instances
// This prevents shared references between different person objects

export const createInitialBasicInfo = (): BasicInfo => ({
  chineseSurname: "",
  chineseGivenName: "",
  englishSurname: "",
  englishGivenName: "",
  birthDate: "",
  gender: undefined,
  maritalStatus: undefined,
  idNumber: "",
  idType: undefined,
});

export const createInitialPersonalInfo = (): PersonalInfo => ({
  phone: "",
  email: "",
  address: "",
  country: "",
  height: undefined,
  weight: undefined,
  smokingQuantity: 0,
  salutation: undefined,
  smoke: undefined,
});

export const createInitialEmployer = (): Employer => ({
  name: "",
  address: "",
  industry: "",
});

export const createInitialWorkInfo = (): WorkInfo => ({
  jobTitle: "",
  retirementAge: "",
  type: undefined,
  employer: createInitialEmployer(),
});

export const createInitialTaxInfo = (): TaxInfo => ({
  countryOfBirth: "",
  countryOfResidence: "",
  nationality: "",
  hkEntryIdType: undefined,
  hkEntryIdNumber: "",
  isHKPermanentResident: undefined,
  hasHKIDCard: undefined,
  isUSCitizen: undefined,
  hasOtherTaxResidency: undefined,
  remark: "",
});

export const createInitialPolicyholder = (): PolicyholderVO => ({
  basic: createInitialBasicInfo(),
  personal: createInitialPersonalInfo(),
  work: createInitialWorkInfo(),
  tax: createInitialTaxInfo(),
  familyMembers: [],
  isVulnerableCustomer: false,
  isProfessionalInvestor: false,
});

export const createInitialInsured = (): InsuredDTO => ({
  basic: createInitialBasicInfo(),
  personal: createInitialPersonalInfo(),
  tax: createInitialTaxInfo(),
});

// Keep old constant names for backward compatibility, but they now create new instances
export const INITIAL_BASIC_INFO = createInitialBasicInfo();
export const INITIAL_PERSONAL_INFO = createInitialPersonalInfo();
export const INITIAL_EMPLOYER = createInitialEmployer();
export const INITIAL_WORK_INFO = createInitialWorkInfo();
export const INITIAL_TAX_INFO = createInitialTaxInfo();
export const INITIAL_POLICYHOLDER = createInitialPolicyholder();
export const INITIAL_INSURED = createInitialInsured();

export const INITIAL_CURRENT_ASSETS: CurrentAssets = {
  cashAndDeposits: undefined,
  liquidInvestments: undefined,
  otherCurrentAssetsName: "",
  otherCurrentAssetsAmount: undefined,
  hasOtherCurrentAssets: false,
};

export const INITIAL_MONTHLY_EXPENSE: MonthlyExpense = {
  personalAndFamilyExpense: undefined,
  mortgageLoanRepayment: undefined,
  insuranceContribution: undefined,
  otherExpenseName: "",
  otherExpenseAmount: undefined,
  hasOtherExpense: false,
};

export const INITIAL_MONTHLY_INCOME: MonthlyIncome = {
  salaryCommissionBonus: undefined,
  rentalIncome: undefined,
  otherIncomeName: "",
  otherIncomeAmount: undefined,
  hasOtherIncome: false,
};

export const INITIAL_LIABILITIES: Liabilities = {
  mortgageLoan: undefined,
  personalCreditCardLoan: undefined,
  otherLiabilityName: "",
  otherLiabilityAmount: undefined,
  hasOtherLiabilities: false,
};

export const INITIAL_NON_CURRENT_ASSETS: NonCurrentAssets = {
  property: undefined,
  otherNonCurrentAssets: undefined,
};

export const INITIAL_PROTECTION: CurrentProtection = {
  lifeInsurance: "",
  criticalIllnessInsurance: "",
  accidentInsurance: "",
  medicalInsuranceType: "",
  medicalInsuranceAmount: undefined,
  otherExistingProtection: "",
  attachmentRemarks: "",
};

export const INITIAL_FINANCIAL: OrderFinancialVO = {
  currentAssets: INITIAL_CURRENT_ASSETS,
  expense: INITIAL_MONTHLY_EXPENSE,
  income: INITIAL_MONTHLY_INCOME,
  liabilities: INITIAL_LIABILITIES,
  nonCurrentAssets: INITIAL_NON_CURRENT_ASSETS,
  premiumFinancing: [],
  protection: INITIAL_PROTECTION,
  suggestionsAndReasons: [],
  transferInsuranceDeclaration: [],
  financialAnalysis: [],
};

export const INITIAL_PRODUCT_PLAN: OrderProductPlanVO = {
  riders: [],
  applySpecialFirstYearDiscount: false,
  companyCode: "",
  companyName: "",
  contributionYears: "",
  currency: "",
  dividendDistributionMethod: "",
  firstPaymentMethod: "",
  firstYearDiscountedPremium: undefined,
  isDdaSupported: false,
  isIncreasingProtectionIncluded: false,
  isPolicyBackdated: false,
  isPremiumFinanced: false,
  isPrepaid: false,
  paymentFrequency: "",
  policyLanguage: "",
  premiumPerPeriod: undefined,
  prepaidAmountInclFirst: undefined,
  productCode: "",
  productName: "",
  protectionPeriod: "",
  renewalPaymentMethod: "",
  sumAssured: undefined,
};

export const INITIAL_RIDER: RiderVO = {
  contributionYears: "",
  currency: "",
  paymentFrequency: "",
  premiumPerPeriod: undefined,
  productCode: "",
  productName: "",
  protectionPeriod: "",
  sumAssured: undefined,
};

export const INITIAL_SIGN_INFO: OrderSignInfo = {
  advisorSignData: "",
  city: undefined,
  photo: "",
  policyholderSignData: "",
};

export const INITIAL_BENEFICIARY: BeneficiaryVO = {
  basic: INITIAL_BASIC_INFO,
  relationship: undefined,
};
